import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Save, Send, Download, Paperclip, X, FileText, Image as ImageIcon, Bold, Italic, List, ListOrdered } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple Rich Text Editor Component
const SimpleRichEditor = ({ value, onChange, readOnly, placeholder }) => {
  const editorRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!value || value === '<p></p>' || value === '');

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
      setIsEmpty(!value || value === '<p></p>' || value === '');
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      setIsEmpty(!content || content === '<br>' || content === '');
      onChange(content);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  return (
    <div className={`border border-slate-300 rounded overflow-hidden ${readOnly ? 'opacity-60 bg-slate-50' : ''}`}>
      {!readOnly && (
        <div className="flex items-center gap-1 p-2 border-b border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={() => execCommand('bold')}
            className="p-2 hover:bg-slate-200 rounded"
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('italic')}
            className="p-2 hover:bg-slate-200 rounded"
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('insertUnorderedList')}
            className="p-2 hover:bg-slate-200 rounded"
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => execCommand('insertOrderedList')}
            className="p-2 hover:bg-slate-200 rounded"
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={handleInput}
          className="min-h-[200px] p-4 focus:outline-none prose max-w-none"
          data-placeholder={placeholder}
          suppressContentEditableWarning={true}
        />
        {isEmpty && !readOnly && (
          <div className="absolute top-4 left-4 text-slate-400 pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

export default function InteractiveWorksheet() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [worksheet, setWorksheet] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [submissionId, setSubmissionId] = useState(null);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    fetchCaseAndWorksheet();
  }, [caseId]);

  const fetchCaseAndWorksheet = async () => {
    try {
      const caseResponse = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(caseResponse.data.case);
      setWorksheet(caseResponse.data.worksheet);

      if (caseResponse.data.worksheet) {
        const submissionResponse = await axios.get(`${API}/worksheet-submissions/case/${caseId}`);
        
        if (submissionResponse.data) {
          setAnswers(submissionResponse.data.answers || []);
          setSubmissionId(submissionResponse.data.id);
          setStatus(submissionResponse.data.status);
          
          if (submissionResponse.data.has_feedback) {
            const feedbackResponse = await axios.get(`${API}/feedback/${submissionResponse.data.id}`);
            setFeedback(feedbackResponse.data);
          }
        } else {
          const questions = parseWorksheetQuestions(caseResponse.data.worksheet.content);
          setAnswers(questions.map((q, idx) => ({ 
            question_number: idx + 1, 
            question: q, 
            answer: '', 
            attachments: [] 
          })));
        }
      }
    } catch (error) {
      toast.error('Failed to load worksheet');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  const parseWorksheetQuestions = (content) => {
    const lines = content.split('\n');
    const questions = [];
    
    lines.forEach(line => {
      const match = line.match(/^(\d+)\.\s*(.+)$/);
      if (match) {
        questions.push(match[2].trim());
      }
    });
    
    return questions.length > 0 ? questions : ['Question 1', 'Question 2', 'Question 3'];
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = { ...newAnswers[index], answer: value };
    setAnswers(newAnswers);
  };

  const handleFileUpload = async (index, files) => {
    const newAnswers = [...answers];
    const existingAttachments = newAnswers[index].attachments || [];
    
    for (let file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max size is 5MB`);
        continue;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const attachment = {
          name: file.name,
          type: file.type,
          size: file.size,
          data: e.target.result
        };
        
        existingAttachments.push(attachment);
        newAnswers[index] = { ...newAnswers[index], attachments: existingAttachments };
        setAnswers([...newAnswers]);
        toast.success(`${file.name} attached`);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAttachment = (answerIndex, attachmentIndex) => {
    const newAnswers = [...answers];
    newAnswers[answerIndex].attachments.splice(attachmentIndex, 1);
    setAnswers(newAnswers);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const submission = {
        id: submissionId || undefined,
        worksheet_id: worksheet.id,
        case_study_id: caseId,
        answers: answers,
        status: 'draft'
      };

      const response = await axios.post(`${API}/worksheet-submissions`, submission);
      
      if (!submissionId) {
        setSubmissionId(response.data.submission_id);
      }
      
      toast.success('Worksheet saved as draft');
    } catch (error) {
      toast.error('Failed to save worksheet');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!submissionId) {
      await handleSave();
    }

    try {
      await axios.post(`${API}/worksheet-submissions/${submissionId}/submit`);
      setStatus('submitted');
      toast.success('Worksheet submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit worksheet');
    }
  };

  const handleDownload = () => {
    const content = generateWorksheetContent();
    const blob = new Blob([content], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${caseData.title}_worksheet_${user.name}.html`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const generateWorksheetContent = () => {
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${caseData.title} - Worksheet</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
          h1 { color: #0F172A; border-bottom: 3px solid #EA580C; padding-bottom: 10px; }
          .meta { background: #F8FAFC; padding: 15px; border-radius: 5px; margin: 20px 0; }
          .question { margin: 30px 0; padding: 20px; border-left: 4px solid #EA580C; background: #FFF; }
          .question-title { font-weight: bold; color: #0F172A; margin-bottom: 10px; }
          .answer { margin: 15px 0; padding: 15px; background: #F8FAFC; border-radius: 5px; }
          .feedback { margin: 10px 0; padding: 10px; background: #FEF3C7; border-left: 3px solid #F59E0B; }
          .grade { display: inline-block; padding: 5px 15px; background: #10B981; color: white; border-radius: 20px; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>${caseData.title} - Student Worksheet</h1>
        <div class="meta">
          <strong>Student:</strong> ${user.name}<br>
          <strong>Email:</strong> ${user.email}<br>
          <strong>Submitted:</strong> ${new Date().toLocaleDateString()}
        </div>
    `;
    
    if (feedback && feedback.grade) {
      html += `<div class="meta"><span class="grade">Grade: ${feedback.grade}</span></div>`;
    }
    
    if (feedback && feedback.overall_feedback) {
      html += `<div class="feedback"><strong>Overall Feedback:</strong> ${feedback.overall_feedback}</div>`;
    }
    
    answers.forEach((answer, idx) => {
      html += `
        <div class="question">
          <div class="question-title">Question ${idx + 1}: ${answer.question}</div>
          <div class="answer">${answer.answer || 'No answer provided'}</div>
      `;
      
      if (feedback && feedback.answer_comments) {
        const comment = feedback.answer_comments.find(c => c.question_number === idx + 1 || c.question_number === String(idx + 1));
        if (comment && comment.comment) {
          html += `<div class="feedback"><strong>Instructor Comment:</strong> ${comment.comment}</div>`;
        }
      }
      
      html += '</div>';
    });
    
    html += '</body></html>';
    return html;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!worksheet) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">No worksheet available for this case</p>
          <Link to={`/cases/${caseId}`} className="text-orange-600 hover:text-orange-700">
            Back to Case
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to={`/cases/${caseId}`} className="flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="back-to-case-link">
              <ArrowLeft className="w-5 h-5" />
              Back to Case
            </Link>
            <div className="flex items-center gap-4">
              {status === 'submitted' ? (
                <span className="px-4 py-2 bg-green-100 text-green-700 rounded font-medium">
                  ✓ Submitted
                </span>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded hover:border-slate-900 transition-all disabled:opacity-50"
                    data-testid="save-draft-button"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-all"
                    data-testid="submit-worksheet-button"
                  >
                    <Send className="w-4 h-4" />
                    Submit
                  </button>
                </>
              )}
              {status === 'submitted' && (
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800 transition-all"
                  data-testid="download-worksheet-button"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-8">
            <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-2" data-testid="worksheet-title">
              {caseData.title} - Student Worksheet
            </h1>
            <p className="text-slate-600">
              Use the rich text editor to format your answers. You can add bold text, lists, and attach files.
            </p>
          </div>

          {feedback && (
            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded" data-testid="feedback-banner">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Instructor Feedback Received</h3>
              {feedback.grade && (
                <div className="mb-2">
                  <span className="inline-block px-4 py-1 bg-green-600 text-white rounded-full font-semibold">
                    Grade: {feedback.grade}
                  </span>
                </div>
              )}
              {feedback.overall_feedback && (
                <p className="text-green-800">{feedback.overall_feedback}</p>
              )}
            </div>
          )}

          <div className="space-y-8">
            {answers.map((answer, index) => (
              <div key={index} className="bg-white p-6 rounded border border-slate-200" data-testid={`question-${index}`}>
                <label className="block text-lg font-semibold text-slate-900 mb-4">
                  Question {index + 1}: {answer.question}
                </label>
                
                <div className="mb-4">
                  <SimpleRichEditor
                    value={answer.answer}
                    onChange={(value) => handleAnswerChange(index, value)}
                    readOnly={status === 'submitted'}
                    placeholder="Type your answer here... You can format text with bold, italic, and lists."
                  />
                </div>

                {status !== 'submitted' && (
                  <div className="mb-4">
                    <label className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded hover:border-slate-900 transition-all cursor-pointer w-fit">
                      <Paperclip className="w-4 h-4" />
                      <span className="text-sm">Attach Files (Images, PDFs, Excel)</span>
                      <input
                        type="file"
                        multiple
                        accept="image/*,.pdf,.xlsx,.xls,.doc,.docx"
                        onChange={(e) => handleFileUpload(index, Array.from(e.target.files))}
                        className="hidden"
                        data-testid={`file-input-${index}`}
                      />
                    </label>
                    <p className="text-xs text-slate-500 mt-1">Max 5MB per file. Supported: Images, PDF, Excel, Word</p>
                  </div>
                )}

                {answer.attachments && answer.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {answer.attachments.map((att, attIdx) => (
                      <div key={attIdx} className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded border border-slate-200">
                        {att.type && att.type.startsWith('image/') ? (
                          <ImageIcon className="w-4 h-4 text-slate-600" />
                        ) : (
                          <FileText className="w-4 h-4 text-slate-600" />
                        )}
                        <span className="text-sm">{att.name}</span>
                        <span className="text-xs text-slate-500">({(att.size / 1024).toFixed(1)}KB)</span>
                        {status !== 'submitted' && (
                          <button
                            onClick={() => removeAttachment(index, attIdx)}
                            className="ml-2 text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {feedback && feedback.answer_comments && (
                  (() => {
                    const comment = feedback.answer_comments.find(c => 
                      c.question_number === index + 1 || c.question_number === String(index + 1)
                    );
                    if (comment && comment.comment) {
                      return (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded">
                          <div className="font-semibold text-amber-900 mb-1">Instructor Feedback</div>
                          <p className="text-amber-800">{comment.comment}</p>
                        </div>
                      );
                    }
                    return null;
                  })()
                )}
              </div>
            ))}
          </div>

          {status === 'submitted' && (
            <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded text-center">
              <h3 className="text-xl font-semibold text-green-900 mb-2">Worksheet Submitted Successfully!</h3>
              <p className="text-green-700">Your instructor will review your submission and provide feedback.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
